import { usePriceStore } from '@/store/price-store';
import { cartService } from '@/services/cart.service';
import { PriceFailList, PriceRequest, PriceResponse, PromotionSync } from '@/types/cart';
import { ApplyPromotionFailCode, Response } from '@/types/base';
import { SyncPriceResponse } from '@/types/price';
import { useAppStore } from '@/store/app-store';
import { ChildCatalogs } from '@/types/product-detail';
import { useCartStore } from '../cart/cart-store';
import { useProductStore } from '../cart/product-store';

const priceFetchingMap = new Map();

export const syncPrice = async (
  childCatalogs: ChildCatalogs[],
  shipToId?: string,
  promotion?: PromotionSync,
  forceCatalogNumbers: string[] = [],
  requestParams?: { pageType?: string }
) => {
  if (promotion?.promotionCode) {
    await fetchPromotion(
      childCatalogs.map((item) => ({
        catalogNumber: item.childCatalogNumber || item.catalogNumber || '',
        webCatalogNumber: item.webCatalogNumber || item.childCatalogNumber || item.catalogNumber || '',
        quantity: item?.quantity || 1,
      })),
      shipToId,
      promotion
    );
  } else {
    const noPriceCatalogArr = getNoPriceCatalogNumbers(childCatalogs, shipToId, forceCatalogNumbers);
    const noPriceCatalogNumbers = noPriceCatalogArr[0];
    const noPriceCatalogItems = noPriceCatalogArr[1];
    const noPriceCatalogNumbersStr = JSON.stringify(noPriceCatalogNumbers);
    if (noPriceCatalogNumbers.length > 0 && !priceFetchingMap.get(noPriceCatalogNumbersStr)) {
      priceFetchingMap.set(noPriceCatalogNumbersStr, true);
      const formatNoPriceCatalogItems = noPriceCatalogItems.map((item) => ({
        catalogNumber: item.childCatalogNumber || item.catalogNumber || '',
        webCatalogNumber: item.webCatalogNumber || item.childCatalogNumber || item.catalogNumber || '',
        quantity: item?.quantity || 1,
      }));
      await fetchPrices(formatNoPriceCatalogItems, shipToId, requestParams);
      priceFetchingMap.delete(noPriceCatalogNumbersStr);
    }
  }

  return generateSyncPriceResponse(childCatalogs);
};

export const evaluateManualPromotionPrice = async (
  itemList: PriceRequest[],
  shipToId?: string,
  promotion?: PromotionSync
) => {
  if (!promotion?.promotionCode) return;

  const billToId = useAppStore.getState()?.billToInfo?.accountNumber;
  const shipTo = shipToId ?? useAppStore.getState()?.shipToList?.find((item) => item.isDefault)?.accountNumber;
  const currentAddress = useCartStore.getState().address;
  const result = await cartService.evaluateManualPromotionPrice(
    itemList,
    shipTo,
    billToId,
    promotion,
    currentAddress?.addressKey
  );

  handleEvaluatePromotionResult(result, itemList, promotion);
};

const normalizeEvaluatePrices = (prices: any[] = []) => {
  return prices.map((price) => ({
    ...price,
    catalogNumber: price.catalogNumber ?? price.sourceCatalogNumber,
    canApplyPromotion:
      price.canApplyPromotion ??
      (price.lineType === 'PROMOTION' ? true : price.lineType === 'REGULAR' ? false : !!price.appliedPromoType),
    includePromotion: price.includePromotion ?? price.appliedPromoCode ?? null,
    promoNotApplicableReason: price.promoNotApplicableReason ?? price.manualPromoNotApplicableReasonCode ?? null,
  }));
};

const buildFBPProductsFromCurrent = (prices: any[] = []) => {
  const repeatedCatalogNumbers = prices.reduce((acc, price) => {
    acc[price.catalogNumber] = (acc[price.catalogNumber] || 0) + 1;
    return acc;
  }, {});
  const repeatedItems = Object.keys(repeatedCatalogNumbers).filter((key) => repeatedCatalogNumbers[key] > 1);
  const repeatedItemsObject = repeatedItems.reduce((acc, catalogNumber) => {
    const items = prices.filter((price) => price.catalogNumber === catalogNumber);
    const canApplyPromotionTrue = items
      .filter((item) => item.canApplyPromotion)
      .reduce((sum, item) => sum + item.quantity, 0);
    const canApplyPromotionFalse = items
      .filter((item) => !item.canApplyPromotion)
      .reduce((sum, item) => sum + item.quantity, 0);
    acc[catalogNumber] = {
      canApplyPromotionTrue,
      canApplyPromotionFalse,
    };
    return acc;
  }, {});

  const currentProducts = useProductStore.getState().products;
  const combinedProducts = currentProducts.reduce((acc, product) => {
    const existingProduct = acc.find((item) => item.catalogNumber === product.catalogNumber);
    if (existingProduct) {
      existingProduct.quantity += product.quantity;
    } else {
      const { canApplyPromotion, ...rest } = product;
      acc.push({ ...rest });
    }
    return acc;
  }, []);

  return combinedProducts.flatMap((product) => {
    const repeatedItem = repeatedItemsObject[product.catalogNumber];
    if (!repeatedItem) {
      return [{ ...product }];
    }

    const nextProducts = [];
    const { canApplyPromotionTrue, canApplyPromotionFalse } = repeatedItem;
    if (canApplyPromotionTrue > 0) {
      nextProducts.push({ ...product, quantity: canApplyPromotionTrue, canApplyPromotion: true });
    }
    if (canApplyPromotionFalse > 0) {
      nextProducts.push({ ...product, quantity: canApplyPromotionFalse, canApplyPromotion: false });
    }
    return nextProducts;
  });
};

const applyFBPSplitProducts = (prices: any[]) => {
  const { setFBPSkuInfo } = usePriceStore.getState();
  setFBPSkuInfo(prices);
  const updatedProducts = buildFBPProductsFromCurrent(prices);
  useProductStore.getState().initProducts(updatedProducts);
};

export const handleEvaluatePromotionResult = (
  result: any,
  itemList: PriceRequest[] = [],
  promotion?: PromotionSync
) => {
  const { batchAddProductPrice, setPromotionInfo, promotionInfo, setFBPSkuInfo } = usePriceStore.getState();
  if (result?.code !== '200') return;

  const prices = normalizeEvaluatePrices(result.data?.successList || []);
  const requestedCatalogNumbers = new Set(itemList.map((item) => item.catalogNumber));
  const currentPromotionMessages = promotionInfo?.promotionMessages || [];
  const nextPromotionMessages = result.data?.promotionMessages || [];
  const mergedPromotionMessages = currentPromotionMessages.filter(
    (item) => !requestedCatalogNumbers.has(item.catalogNumber)
  );
  nextPromotionMessages.forEach((message) => {
    const existingIndex = mergedPromotionMessages.findIndex(
      (item) => item.catalogNumber === message.catalogNumber && item.messageCode === message.messageCode
    );
    if (existingIndex >= 0) {
      mergedPromotionMessages[existingIndex] = message;
    } else {
      mergedPromotionMessages.push(message);
    }
  });

  const manualPromotion = result.data?.manualPromotion || {};
  const effectivePromoType = manualPromotion?.promoType ?? promotionInfo?.promoType ?? null;
  setPromotionInfo({
    promoCode: manualPromotion?.promoCode ?? promotion?.promotionCode ?? promotionInfo?.promoCode ?? null,
    promoDiscount: promotionInfo?.promoDiscount ?? null,
    promoName: promotionInfo?.promoName ?? null,
    promoBizKey: manualPromotion?.promoBizKey ?? promotionInfo?.promoBizKey ?? null,
    promoType: effectivePromoType,
    promoDesc: promotionInfo?.promoDesc ?? null,
    isShowPromoCode: true,
    qtpSKUMap: promotionInfo?.qtpSKUMap ?? null,
    promotionMessages: mergedPromotionMessages,
  });

  batchAddProductPrice(prices);
  handleFailList(result.data?.failList || []);

  if (effectivePromoType === 'FBP') {
    applyFBPSplitProducts(prices);
  } else {
    setFBPSkuInfo([]);
  }
};

const generateSyncPriceResponse = (childCatalogs: ChildCatalogs[]) => {
  const { productPrice } = usePriceStore.getState();
  const response: SyncPriceResponse = {
    successList: [],
    failList: [],
  };
  childCatalogs.forEach((item) => {
    if (productPrice[item?.childCatalogNumber]) {
      if (productPrice[item?.childCatalogNumber].hasError) {
        response.failList.push(item?.webCatalogNumber || item?.childCatalogNumber || item?.catalogNumber);
      } else {
        response.successList.push(productPrice[item.childCatalogNumber]);
      }
    }
  });
  return response;
};

const getNoPriceCatalogNumbers = (
  childCatalogs: ChildCatalogs[],
  shipToId: string,
  forceCatalogNumbers: string[] = []
) => {
  const { productPrice, priceRefreshDepend, setPriceRefreshDepend, batchRemovePrice } = usePriceStore.getState();
  const shipToChanged = priceRefreshDepend?.shipTo !== shipToId;
  const needRefresh = shipToChanged;
  const forceCatalogNumberSet = new Set(forceCatalogNumbers);

  const filterItemsNeedingPriceUpdate = childCatalogs.filter((item) => {
    return (
      forceCatalogNumberSet.has(item.childCatalogNumber) ||
      !productPrice[item.childCatalogNumber] ||
      productPrice[item.childCatalogNumber].hasError
    );
  });

  const filteredCatalogNumbers = filterItemsNeedingPriceUpdate.map((item) => item.childCatalogNumber).sort();

  if (needRefresh) {
    const catalogNumbers = childCatalogs.map((item) => item.childCatalogNumber);
    batchRemovePrice(catalogNumbers);
    setPriceRefreshDepend({
      shipTo: shipToId,
    });

    return [catalogNumbers, childCatalogs];
  } else {
    return [filteredCatalogNumbers, filterItemsNeedingPriceUpdate];
  }
};

const fetchPromotion = async (
  formatNoPriceCatalogItems: PriceRequest[],
  shipToId: string,
  promotion: PromotionSync
) => {
  const billToId = useAppStore.getState()?.billToInfo?.accountNumber;
  const shipTo = shipToId ?? useAppStore.getState()?.shipToList?.find((item) => item.isDefault)?.accountNumber;
  const currentAddress = useCartStore.getState().address;
  const { setPromotionError, setPromotionInfo, batchAddProductPrice, setFBPSkuInfo } = usePriceStore.getState();

  const result = await cartService.getProductPriceByPromoCode(
    formatNoPriceCatalogItems,
    shipTo,
    billToId,
    promotion,
    currentAddress?.addressKey
  );

  if (result.code === '200') {
    const prices = result.data.successList;
    const promotionInfo = result.data.promotionInfo || null;
    const hasPromotionMessages = !!promotionInfo?.promotionMessages?.length;
    if (prices.some((obj) => obj.canApplyPromotion === true)) {
      setPromotionInfo({ ...promotionInfo, isShowPromoCode: true });
    } else {
      if (promotion?.promotionCode) {
        setPromotionInfo({ ...promotionInfo, isShowPromoCode: false });
      } else if (hasPromotionMessages) {
        setPromotionInfo({ ...promotionInfo, isShowPromoCode: false });
      } else {
        setPromotionInfo(null);
      }
    }
    batchAddProductPrice(prices);
    handleFailList(result.data?.failList || []);
    if (promotionInfo && promotionInfo.promoType === 'FBP') {
      setFBPSkuInfo(prices);
      const updatedProducts = buildFBPProductsFromCurrent(prices);
      useProductStore.getState().initProducts(updatedProducts);
    } else {
      setFBPSkuInfo([]);
    }
  } else {
    setPromotionError(result.code as ApplyPromotionFailCode);
  }
};

const fetchPrices = async (
  formatNoPriceCatalogItems: PriceRequest[],
  shipToId: string,
  requestParams?: { pageType?: string }
) => {
  for (let i = 1; i <= Math.ceil(formatNoPriceCatalogItems.length / 10); i++) {
    const batch = formatNoPriceCatalogItems.slice((i - 1) * 10, i * 10);
    const result = await getPrices(batch, shipToId, requestParams);
    handleFetchResult(result, batch);
  }
};

const getPrices = async (batch: PriceRequest[], shipToId: string, requestParams?: { pageType?: string }) => {
  const billToId = useAppStore.getState()?.billToInfo?.accountNumber;
  const shipTo = shipToId ?? useAppStore.getState()?.shipToList?.find((item) => item.isDefault)?.accountNumber;
  const result = await cartService.getProductPrice(batch, shipTo, billToId, requestParams);
  if (result.code !== '200') {
    handleFailList(
      batch.map((item) => ({ catalogNumber: item.webCatalogNumber || item.catalogNumber, errorMessage: '' }))
    );
  }
  return result;
};

export const handleFetchResult = (result: Response<PriceResponse>, batch: PriceRequest[] = []) => {
  const { batchAddProductPrice, promotionInfo, setPromotionInfo } = usePriceStore.getState();
  if (result.code === '200') {
    const prices = result.data.successList;
    const nextPromotionInfo = result.data.promotionInfo;
    const nextPromotionMessages = nextPromotionInfo?.promotionMessages || [];
    const requestedCatalogNumbers = new Set(batch.map((item) => item.catalogNumber));

    if (batch.length > 0 || nextPromotionMessages.length > 0) {
      const currentPromotionMessages = promotionInfo?.promotionMessages || [];
      const mergedPromotionMessages = currentPromotionMessages.filter(
        (item) => !requestedCatalogNumbers.has(item.catalogNumber)
      );

      nextPromotionMessages.forEach((message) => {
        const existingIndex = mergedPromotionMessages.findIndex(
          (item) => item.catalogNumber === message.catalogNumber && item.messageCode === message.messageCode
        );
        if (existingIndex >= 0) {
          mergedPromotionMessages[existingIndex] = message;
        } else {
          mergedPromotionMessages.push(message);
        }
      });

      setPromotionInfo({
        promoCode: nextPromotionInfo?.promoCode ?? promotionInfo?.promoCode ?? null,
        promoDiscount: nextPromotionInfo?.promoDiscount ?? promotionInfo?.promoDiscount ?? null,
        promoName: nextPromotionInfo?.promoName ?? promotionInfo?.promoName ?? null,
        promoBizKey: nextPromotionInfo?.promoBizKey ?? promotionInfo?.promoBizKey ?? null,
        promoType: nextPromotionInfo?.promoType ?? promotionInfo?.promoType ?? null,
        promoDesc: nextPromotionInfo?.promoDesc ?? promotionInfo?.promoDesc ?? null,
        isShowPromoCode: false,
        qtpSKUMap: nextPromotionInfo?.qtpSKUMap ?? promotionInfo?.qtpSKUMap ?? null,
        promotionMessages: mergedPromotionMessages,
      });
    }

    batchAddProductPrice(prices);
    handleFailList(result.data?.failList || []);
  }
};

const handleFailList = (failList: PriceFailList[]) => {
  if (failList?.length > 0) {
    usePriceStore.getState().batchAddProductPrice(
      failList.map((item) => ({
        catalogNumber: item.catalogNumber,
        listPrice: 0,
        bestPrice: 0,
        itemSubTotal: 0,
        currency: '',
        priceStatus: 'ContactUs',
        quantity: 1,
        hasError: true,
      }))
    );
  }
};

export const removeSplitCart = () => {
  const products = useProductStore.getState().products;
  const combinedProducts = products.reduce((acc, product) => {
    const existingProduct = acc.find((item) => item.catalogNumber === product.catalogNumber);
    if (existingProduct) {
      existingProduct.quantity += product.quantity;
    } else {
      const { canApplyPromotion, ...rest } = product;
      acc.push({ ...rest });
    }
    return acc;
  }, []);
  useProductStore.getState().initProducts(combinedProducts);
};
